import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { imagineStore } from "@/stores/imagine.store";
import type { UpdateProjectFileInput } from "@imagine/ai-service/shared-types/route-types";
import { useSaveProjectFiles } from "../code-panel/use-save-project-files";
import { useImagineChat } from "@/providers/imagine-chat-provider";

type PendingChangesBarProps = {
  hasPendingDrafts: boolean;
  draftStateKey: string;
  onSave: () => void;
  onDiscard: () => void;
  isSaving: boolean;
  saveDisabled: boolean;
  discardDisabled: boolean;
};

type UnsavedChangesDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  onDiscard: () => void;
  saveDisabled: boolean;
  isSaving: boolean;
};

type CanvasPanelController = {
  projectId: string | null;
  previewUrlWithPreviewToken: URL | null;
  sandboxInstrumented: boolean;
  isInProgress: boolean;
  uiMode: "preview" | "code";
  displayWorkspaceStateOverlay: boolean;
  codeSelectedFile: string | null;
  setCodeSelectedFile: (path: string | null) => void;
  onRequestModeChange: (mode: "preview" | "code") => void;
  pendingChangesBarProps: PendingChangesBarProps;
  unsavedDialogProps: UnsavedChangesDialogProps;
  previewUrl: URL | null;
};

export function useCanvasPanelController(): CanvasPanelController {
  const isReadonly = imagineStore.use.isReadonly();
  const [codeSelectedFile, setCodeSelectedFile] = useState<string | null>(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavMode, setPendingNavMode] = useState<"preview" | null>(null);
  const workspaceStateSteps = imagineStore.use.workspaceState.steps();

  const project = imagineStore.use.project();
  const projectId = imagineStore.use.projectId();
  const sandboxInstrumented = imagineStore.use.sandboxState.instrumented();
  const isInProgress = imagineStore.use.workspaceState.isInProgress();
  const previewUrl = imagineStore.use.previewState.previewUrl();
  const previewUrlWithPreviewToken =
    imagineStore.use.previewState.previewUrlWithPreviewToken();
  const uiMode = imagineStore.use.ui.mode();
  const setMode = imagineStore.use.ui.setMode();
  const drafts = imagineStore.use.codeEditor.drafts();
  const clearAllDrafts = imagineStore.use.codeEditor.clearAll();
  const onFinish$ = imagineStore.use.chat.onFinish$();
  const queryClient = useQueryClient();
  const { isGenerating } = useImagineChat();
  const refreshIframe = imagineStore.use.previewState.refreshIframe();

  const displayWorkspaceStateOverlay = useMemo(() => {
    // Still fetching the project, we want to show the overlay
    if (!project) {
      return true;
    }

    // Readonly mode, and project is deployed on Appwrite Sites, no reason to show the overlay
    // because the preview will be served from Appwrite Sites
    if (isReadonly && Boolean(project.appwriteSiteUrl)) {
      return false;
    }

    const hasWorkspaceStateSteps = workspaceStateSteps?.length > 0;

    const shouldDisplay =
      !previewUrlWithPreviewToken ||
      !previewUrl ||
      !sandboxInstrumented ||
      isInProgress ||
      hasWorkspaceStateSteps;

    // console.table({
    //   shouldDisplay,
    //   "!previewUrlWithPreviewToken": !previewUrlWithPreviewToken,
    //   "!previewUrl": !previewUrl,
    //   "!sandboxInstrumented": !sandboxInstrumented,
    //   isInProgress: isInProgress,
    // });

    return shouldDisplay;
  }, [
    previewUrlWithPreviewToken,
    sandboxInstrumented,
    isInProgress,
    isReadonly,
    project,
  ]);

  useEffect(() => {
    if (!displayWorkspaceStateOverlay) {
      refreshIframe();
    }
  }, [displayWorkspaceStateOverlay, refreshIframe]);

  const pendingDraftArray = useMemo(
    () =>
      Object.entries(drafts).map(([path, draft]) => ({
        path,
        content: draft.content,
        createIfNotExists: draft.createIfNotExists,
      })),
    [drafts],
  );

  const hasPendingChanges = pendingDraftArray.length > 0;
  const draftStateKey = useMemo(() => {
    const entries = Object.entries(drafts);
    if (entries.length === 0) return "0:0";
    let totalLen = 0;
    for (const [, draft] of entries) totalLen += draft.content.length;
    return `${entries.length}:${totalLen}`;
  }, [drafts]);

  const invalidateProjectFiles = useCallback(
    async (targetProjectId: string | null) => {
      if (!targetProjectId) return;
      await queryClient.invalidateQueries({
        queryKey: ["project-files", targetProjectId],
      });
    },
    [queryClient],
  );

  useEffect(() => {
    if (!projectId) {
      return;
    }
    const subscription = onFinish$.subscribe(() => {
      const currentMode = imagineStore.getState().ui.mode;
      if (currentMode === "code" && sandboxInstrumented) {
        return;
      }
      void invalidateProjectFiles(projectId);
    });

    return () => subscription.unsubscribe();
  }, [onFinish$, projectId, sandboxInstrumented, invalidateProjectFiles]);

  const saveMutation = useSaveProjectFiles(projectId);

  const isSaving = saveMutation.isPending;
  const saveDisabled =
    !sandboxInstrumented || !hasPendingChanges || isSaving || !projectId;
  const discardDisabled = !hasPendingChanges || isSaving;

  const handleSave = useCallback(() => {
    if (!projectId || !pendingDraftArray.length || saveMutation.isPending) {
      return;
    }

    const payload: Array<UpdateProjectFileInput> = pendingDraftArray.map(
      ({ path, content, createIfNotExists }) => ({
        path,
        content,
        ...(createIfNotExists ? { createIfNotExists: true } : {}),
      }),
    );
    saveMutation.mutate(payload, {
      onSuccess: async (data) => {
        clearAllDrafts();
        if (data.warnings?.length) {
          data.warnings.forEach((warning) =>
            console.warn(`[imagine] Warning: ${warning}`),
          );
        }
        if (data.commit) {
          console.info(
            `[imagine] saved ${data.commit.shortSha}: ${data.commit.message}`,
          );
        }
        await invalidateProjectFiles(projectId);
      },
      onError: (error) => {
        console.error(`[imagine] Failed to save changes: ${error.message}`);
      },
    });
  }, [pendingDraftArray, projectId, saveMutation]);

  const handleDiscard = useCallback(() => {
    if (!hasPendingChanges || saveMutation.isPending) {
      return;
    }

    clearAllDrafts();
    void invalidateProjectFiles(projectId);
  }, [
    hasPendingChanges,
    saveMutation.isPending,
    clearAllDrafts,
    invalidateProjectFiles,
    projectId,
  ]);

  useEffect(() => {
    if (pendingNavMode && !hasPendingChanges && !isSaving) {
      setShowUnsavedDialog(false);
      setPendingNavMode(null);
      setMode(pendingNavMode);
    }
  }, [pendingNavMode, hasPendingChanges, isSaving, setMode]);

  useEffect(() => {
    setCodeSelectedFile(null);
    clearAllDrafts();
  }, [projectId, clearAllDrafts]);

  useEffect(() => {
    if (!isGenerating) {
      return;
    }

    if (uiMode === "code") {
      setMode("preview");
    }
  }, [isGenerating, uiMode, setMode]);

  useEffect(() => {
    if (!hasPendingChanges) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.returnValue =
        "You have pending changes that are not committed. Save them before leaving.";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasPendingChanges]);

  const guardedSetMode = useCallback(
    (mode: "preview" | "code") => {
      if (mode === "preview" && hasPendingChanges) {
        setPendingNavMode("preview");
        setShowUnsavedDialog(true);
        return;
      }
      setMode(mode);
    },
    [hasPendingChanges, setMode],
  );

  const closeUnsavedDialog = useCallback(() => {
    setShowUnsavedDialog(false);
    setPendingNavMode(null);
  }, []);

  const confirmUnsavedDialogSave = useCallback(() => {
    setShowUnsavedDialog(true);
    handleSave();
  }, [handleSave]);

  const confirmUnsavedDialogDiscard = useCallback(() => {
    setShowUnsavedDialog(true);
    handleDiscard();
  }, [handleDiscard]);

  const pendingChangesBarProps: PendingChangesBarProps = {
    hasPendingDrafts: hasPendingChanges,
    draftStateKey,
    onSave: handleSave,
    onDiscard: handleDiscard,
    isSaving,
    saveDisabled,
    discardDisabled,
  };

  const unsavedDialogProps: UnsavedChangesDialogProps = {
    open: showUnsavedDialog,
    onClose: closeUnsavedDialog,
    onSave: confirmUnsavedDialogSave,
    onDiscard: confirmUnsavedDialogDiscard,
    saveDisabled,
    isSaving,
  };

  return {
    projectId,
    previewUrl,
    previewUrlWithPreviewToken,
    sandboxInstrumented,
    isInProgress,
    uiMode,
    displayWorkspaceStateOverlay,
    codeSelectedFile,
    setCodeSelectedFile,
    onRequestModeChange: guardedSetMode,
    pendingChangesBarProps,
    unsavedDialogProps,
  };
}
